export type AuthUser = { id: string; name: string; email: string } | null;

type StoredUser = { id: string; name: string; email: string; passwordHash: string };

const STORAGE_KEY = 'mb_auth_user';
const USERS_KEY = 'mb_users';

function readUsers(): StoredUser[] {
    try {
        const raw = localStorage.getItem(USERS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function writeUsers(users: StoredUser[]) {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function hash(input: string): string {
    // Simple non-secure hash for demo only
    let h = 0;
    for (let i = 0; i < input.length; i++) {
        h = (h << 5) - h + input.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h).toString(36);
}

export function getStoredUser(): AuthUser {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export async function register(name: string, email: string, password: string): Promise<AuthUser> {
    if (!name || !email || !password) {
        throw new Error('All fields are required');
    }
    await new Promise(r => setTimeout(r, 500));

    const users = readUsers();
    const exists = users.some(u => u.email.toLowerCase() === email.toLowerCase());
    if (exists) {
        throw new Error('Email already registered');
    }

    const newUser: StoredUser = {
        id: 'u_' + Math.random().toString(36).slice(2, 9),
        name,
        email,
        passwordHash: hash(password)
    };
    users.push(newUser);
    writeUsers(users);

    const sessionUser: AuthUser = { id: newUser.id, name: newUser.name, email: newUser.email };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionUser));
    return sessionUser;
}

export async function login(email: string, password: string): Promise<AuthUser> {
    if (!email || !password) {
        throw new Error('Email and password are required');
    }
    await new Promise(r => setTimeout(r, 400));

    const users = readUsers();
    const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (!user || user.passwordHash !== hash(password)) {
        throw new Error('Invalid email or password');
    }

    const sessionUser: AuthUser = { id: user.id, name: user.name, email: user.email };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionUser));
    return sessionUser;
}

export function logout(): void {
    localStorage.removeItem(STORAGE_KEY);
}
